import { z } from 'zod'
import { api } from 'roadie-ui'
import { apiGet } from 'util/server/apiFactory'

export type BlockPayment = {
  id: number
  scheduledStartTime: Date | null
  scheduledEndTime: Date | null
  driverStartTime: Date
  driverEndTime: Date
  depotName: string
  gigIds: number[]
  consolidationIds: number[]
  payments: Payment[]
}

export type Payment = {
  paymentId: number
  gigId: number
  consolidationId: number
  payoutId: string | null
  paymentCategory: string
  paymentStatus: string
  paymentAmount: number
  paymentCreatedAt: Date
  paymentUpdatedAt: Date
}

export type PaymentCategoryGroup = {
  [key: string]: Payment[]
}

// Mapping from our camelCase property names to block payment API snake_case field names
export const PAYMENT_CATEGORY_MAP = {
  reconciliation: 'reconciliation',
  gigPay: 'gig_pay',
  reimbursement: 'reimbursement',
  tip: 'tip',
} as const

export const PAYMENT_STATUS_MAP = {
  held: 'held',
  initialized: 'initialized',
  settled: 'settled',
  submitted: 'submitted',
  returned: 'returned',
} as const

// Define constants for the API names of the payment statuses and categories as lists
const PAYMENT_STATUSES = Object.keys(PAYMENT_STATUS_MAP) as (keyof typeof PAYMENT_STATUS_MAP)[]

const PaymentSchema = z.object({
  payment_id: z.number(),
  gig_id: z.number(),
  consolidation_id: z.number(),
  payout_id: z.string().nullable(),
  payment_category: z.string(),
  payment_status: z.string(),
  payment_amount: z.number(),
  payment_created_at: z.string(),
  payment_updated_at: z.string(),
})

const PaymentCategoryGroupSchema = z.record(z.string(), z.array(PaymentSchema))

const PaymentsSchema = z.record(z.string(), PaymentCategoryGroupSchema.optional())

const BlockPaymentItemSchema = z.object({
  id: z.number(),
  scheduled_start_time: z.string().nullable(),
  scheduled_end_time: z.string().nullable(),
  depot_name: z.string(),
  driver_start_time: z.string().nullable(),
  driver_end_time: z.string().nullable(),
  gig_ids: z.array(z.number()),
  consolidation_ids: z.array(z.number()),
  payments: PaymentsSchema,
})

export const BlockPaymentDataForDriverResponseSchema = z.object({
  current_page: z.number(),
  per_page: z.number(),
  total_pages: z.number(),
  total_count: z.number(),
  data: z.array(BlockPaymentItemSchema),
})

type BlockPaymentDataForDriverResponse = z.infer<typeof BlockPaymentDataForDriverResponseSchema>

// Helper function to map API/DB payment category to internal category
function getGeneralPaymentCategory(dbPaymentCategory: string): string {
  switch (dbPaymentCategory) {
    case 'base_payout':
    case 'cancellation_payout':
    case 'roadies_cut_adjustment':
    case 'seattle_minimum_pay_adjustment':
      return PAYMENT_CATEGORY_MAP.gigPay

    case 'promotional_credit':
    case 'reconciliation':
    case 'prop22_earnings_adjustment':
    case 'seattle_premium':
    case 'seattle_psst':
    case 'nyc_earnings_adjustment':
      return PAYMENT_CATEGORY_MAP.reconciliation

    case 'expense_adjustment':
    case 'toll_adjustment':
    case 'reimbursement':
      return PAYMENT_CATEGORY_MAP.reimbursement

    case 'guaranteed_tip':
    case 'tip':
    case 'net_tip':
    case 'incidentals':
      return PAYMENT_CATEGORY_MAP.tip

    default:
      return dbPaymentCategory
  }
}

// Helper function to transform a single payment
const transformPayment = (p: any): Payment => ({
  paymentId: p.payment_id,
  gigId: p.gig_id,
  consolidationId: p.consolidation_id,
  payoutId: p.payout_id,
  paymentCategory: p.payment_category,
  paymentStatus: p.payment_status,
  paymentAmount: p.payment_amount,
  paymentCreatedAt: new Date(p.payment_created_at),
  paymentUpdatedAt: new Date(p.payment_updated_at),
})

// Helper function to transform a payment category group
const transformPaymentCategoryGroup = (categoryGroup: any): PaymentCategoryGroup => {
  const result: PaymentCategoryGroup = {}

  // Dynamically handle all payment categories from the API response
  if (categoryGroup) {
    Object.entries(categoryGroup).forEach(([category, payments]) => {
      if (Array.isArray(payments)) {
        result[category] = payments.map(transformPayment)
      }
    })
  }

  return result
}

// Helper function to transform payments object
const transformPayments = (paymentsData: any) => {
  const result: BlockPaymentForDriver['payments'] = {}

  for (const status of PAYMENT_STATUSES) {
    if (paymentsData?.[status]) {
      result[status] = transformPaymentCategoryGroup(paymentsData[status])
    }
  }

  return result
}

export type BlockPaymentForDriver = {
  id: number
  scheduledStartTime: Date | null
  scheduledEndTime: Date | null
  depotName: string
  driverStartTime: Date | null
  driverEndTime: Date | null
  gigIds: number[]
  payments: {
    held?: PaymentCategoryGroup
    initialized?: PaymentCategoryGroup
    settled?: PaymentCategoryGroup
    submitted?: PaymentCategoryGroup
    returned?: PaymentCategoryGroup
  }
  consolidationIds: number[]
}

export type BlockPaymentDataForDriver = {
  currentPage: number
  perPage: number
  totalPages: number
  totalCount: number
  data: BlockPaymentForDriver[]
}

/**
 * Deserializes the validated API response data into a BlockPaymentDataForDriver object.
 * This function transforms snake_case API fields to camelCase and converts date strings to Date objects.
 */
export function deserializeBlockPaymentDataForDriver(data: unknown): BlockPaymentDataForDriver {
  const validatedData = data as BlockPaymentDataForDriverResponse
  return {
    currentPage: validatedData.current_page,
    perPage: validatedData.per_page,
    totalPages: validatedData.total_pages,
    totalCount: validatedData.total_count,
    data: validatedData.data.map(
      (item): BlockPaymentForDriver => ({
        id: item.id,
        scheduledStartTime: item.scheduled_start_time ? new Date(item.scheduled_start_time) : null,
        scheduledEndTime: item.scheduled_end_time ? new Date(item.scheduled_end_time) : null,
        depotName: item.depot_name,
        driverStartTime: item.driver_start_time ? new Date(item.driver_start_time) : null,
        driverEndTime: item.driver_end_time ? new Date(item.driver_end_time) : null,
        gigIds: item.gig_ids,
        consolidationIds: item.consolidation_ids,
        payments: transformPayments(item.payments),
      })
    ),
  }
}

export async function fetchBlockPaymentData(blockId: number): Promise<BlockPayment> {
  return await api
    .version(3)
    .get(`/block_payments/get_by_id?id=${blockId}`)
    .then((resp: any) => {
      const data = resp.data
      if (data.length == 0) {
        return Promise.reject(new Error('No block payment data found'))
      }
      const blockPaymentData: BlockPayment = {
        id: Number(data.id),
        scheduledStartTime: data.scheduled_start_time ? new Date(data.scheduled_start_time) : null,
        scheduledEndTime: data.scheduled_end_time ? new Date(data.scheduled_end_time) : null,
        driverStartTime: new Date(data.driver_start_time),
        driverEndTime: new Date(data.driver_end_time),
        depotName: data.depot_name,
        gigIds: data.gig_ids,
        consolidationIds: data.consolidation_ids,
        payments: data.payments.map(
          (payment: any): Payment => ({
            paymentId: payment.payment_id,
            gigId: payment.gig_id,
            consolidationId: payment.consolidation_id,
            payoutId: payment.payout_id,
            paymentCategory: getGeneralPaymentCategory(payment.payment_category),
            paymentStatus: payment.payment_status,
            paymentAmount: payment.payment_amount,
            paymentCreatedAt: new Date(payment.payment_created_at),
            paymentUpdatedAt: new Date(payment.payment_updated_at),
          })
        ),
      }
      return blockPaymentData
    })
}

/**
 * Helper function to process block payments by status and convert them to a list of transactions
 * @param paymentsForStatus - Payments grouped by category (reconciliation, basePayout, etc.)
 * @param status - Payment status (held, initialized, settled, submitted, returned)
 * @param blockPaymentData - Block payment metadata
 * @param maskedAccountNumber - Masked account number from matching regular transaction
 * @returns Array of transformed payment transactions
 */
export function transformBlockPaymentsToTransactions(
  paymentsForStatus: PaymentCategoryGroup | undefined,
  status: string,
  blockPaymentData: BlockPaymentForDriver,
  maskedAccountNumber: string = ''
): any[] {
  const blockPayments: any[] = []

  if (!paymentsForStatus) return blockPayments

  // Process each payment category (reconciliation, basePayout, reimbursement, tip)
  Object.entries(paymentsForStatus).forEach(([category, paymentsArray]) => {
    if (!paymentsArray || !Array.isArray(paymentsArray)) return

    paymentsArray.forEach((payment) => {
      blockPayments.push({
        id: `block-${blockPaymentData.id}-${category}-${payment.paymentId}`,
        block_id: blockPaymentData.id,
        block_scheduled_start_time: blockPaymentData.scheduledStartTime,
        block_scheduled_end_time: blockPaymentData.scheduledEndTime,
        driver_start_time: blockPaymentData.driverStartTime,
        driver_end_time: blockPaymentData.driverEndTime,
        status: status,
        category: payment.paymentCategory,
        amount: payment.paymentAmount,
        created_at: payment.paymentCreatedAt,
        updated_at: payment.paymentUpdatedAt,
        payout_id: payment.payoutId,
        masked_account_number: maskedAccountNumber,
      })
    })
  })

  return blockPayments
}

export async function fetchBlockPaymentDataForDriver(
  driverId: number,
  page: number,
  perPage: number
): Promise<BlockPaymentDataForDriver> {
  const result = await apiGet(
    api,
    3,
    '/block_payments/get_by_driver_id',
    BlockPaymentDataForDriverResponseSchema,
    deserializeBlockPaymentDataForDriver,
    {
      driver_id: driverId,
      page,
      per_page: perPage,
    }
  )

  return (
    result.data ?? {
      currentPage: page,
      perPage: perPage,
      totalPages: page - 1,
      totalCount: 0,
      data: [],
    }
  )
}

export type BlockData = {
  blockId: number
  startedAt: Date | null
  scheduledStart: Date | null
  endedAt: Date | null
  scheduledEnd: Date | null
}

export async function fetchBlockData(gigId: number): Promise<BlockData> {
  return await api
    .version(3)
    .get(`/block_payments/get_by_gig_id?gig_id=${gigId}`)
    .then((resp: any) => {
      const data = resp.data
      if (data.length == 0) {
        return Promise.reject(new Error('No block payment data found'))
      }
      const blockData: BlockData = {
        blockId: data.block_payment_id,
        startedAt: data.started_at ? new Date(data.started_at) : null,
        scheduledStart: data.scheduled_start ? new Date(data.scheduled_start) : null,
        endedAt: data.ended_at ? new Date(data.ended_at) : null,
        scheduledEnd: data.scheduled_end ? new Date(data.scheduled_end) : null,
      }
      return blockData
    })
}
